// pages/TractateDetail.js
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../auth';
import DomainSwitch, { pickVisibleDomain, visibleDomains } from '../components/DomainSwitch';
import Breadcrumbs from '../components/Breadcrumbs';

export default function TractateDetail() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tractate, setTractate] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // domain: 'talmud' (תלמוד) or 'toshba' (תושב"ע)
  const domainFromUrl = searchParams.get('domain');
  const [domain, setDomain] = useState(() =>
    (domainFromUrl || localStorage.getItem('domain') || 'toshba').toLowerCase()
  );

  // keep URL & localStorage in sync when domain changes
  useEffect(() => {
    localStorage.setItem('domain', domain);
    const current = (searchParams.get('domain') || '').toLowerCase();
    if (current !== domain) {
      const next = new URLSearchParams(searchParams);
      next.set('domain', domain);
      setSearchParams(next, { replace: true });
    }
  }, [domain, searchParams, setSearchParams]);

  // load tractate
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/`);
        if (!res.ok) throw new Error('failed');
        const data = await res.json();
        if (!ignore) setTractate(data);
      } catch {
        if (!ignore) setError('שגיאה בטעינת פרטי המסכת');
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [id]);

  // if the URL/localStorage domain is hidden for this tractate, switch to a visible one
  useEffect(() => {
    if (!tractate) return;
    const next = pickVisibleDomain(domain, tractate);
    if (next && next !== domain) setDomain(next);
  }, [tractate, domain]);

  // load chapters for current domain
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/tractates/${id}/chapters/?domain=${domain}`);
        if (!res.ok) throw new Error('chapters failed');
        const data = await res.json();
        if (!ignore) setChapters(data);
      } catch {
        if (!ignore) setChapters([]);
      }
    })();
    return () => { ignore = true; };
  }, [id, domain]);

  // load tractate-level auxiliary folders for current domain
  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        const qs = new URLSearchParams({ tractate: String(id), domain });
        const res = await fetchWithAuth(`/api/auxiliary-folders/?${qs}`);
        const data = res.ok ? await res.json() : [];
        if (!ignore) setFolders(data);
      } catch {
        if (!ignore) setFolders([]);
      }
    })();
    return () => { ignore = true; };
  }, [id, domain]);

  if (loading) return <div className="container"><p className="muted">טוען…</p></div>;
  if (error)   return <div className="container"><p style={{ color: 'crimson' }}>{error}</p></div>;
  if (!tractate) return null;

  const availableDomains = visibleDomains(tractate);
  if (availableDomains.length === 0) {
    return (
      <div className="container">
        <Breadcrumbs
          items={[
            { to: '/tractates', label: 'מסכתות' },
            { label: tractate?.name || '...' },
          ]}
        />
        <div className="toolbar toolbar--center">
          <h2 style={{ marginInlineStart: 0 }}>{tractate.name}</h2>
        </div>
        <p className="muted center" style={{ marginTop: '1rem' }}>
          אין מסלולים להצגה במסכת זו.
        </p>
      </div>
    );
  }

  const plansCount = (tractate.yearly_plans || []).filter(
    (p) => String(p?.domain || '').toLowerCase() === domain
  ).length;

  // fallback image helper (avoid infinite loop)
  const fallbackOnce = (e, fallbackUrl) => {
    const img = e.currentTarget;
    if (img.dataset.fallbackApplied) {
      img.style.display = 'none';
      return;
    }
    img.dataset.fallbackApplied = '1';
    img.src = fallbackUrl;
  };

  return (
    <div className="container">
      <Breadcrumbs
        items={[
          { to: '/tractates', label: 'מסכתות' },
          { label: tractate?.name || '...' }, // current page
        ]}
      />

      {/* Title */}
      <div className="toolbar toolbar--center">
        <h2 style={{ marginInlineStart: 0 }}>{tractate.name}</h2>
      </div>

      {/* Domain switch below title — hidden when only one domain is visible */}
      <div className="center" style={{ marginTop: '.5rem' }}>
        <DomainSwitch
          value={domain}
          onChange={setDomain}
          domains={visibleDomains(tractate)}
        />
      </div>

      {/* Back link */}
      <p className="center" style={{ marginTop: '.5rem' }}>
        <Link to="/tractates">חזרה לרשימת המסכתות</Link>
      </p>

      {/* Subtitle */}
      <div className="stack-3" style={{ marginTop: '1.25rem' }}>
        <p className="muted">
          {domain === 'toshba' ? 'בחרו פרק במסלול תושב"ע:' : 'בחרו פרק במסלול תלמוד:'}
        </p>
      </div>

      <div className="grid-autofit" style={{ marginTop: '1rem' }}>
        {/* Year Plan — both domains (pass state) */}
        <article className="c-card">
          <Link
            to={`/tractates/${id}/year-plan?domain=${domain}`}
            state={{ tractateName: tractate.name }}
            aria-label="תוכנית שנתית"
          >
            <img
              className="c-card__media"
              src={`/assets/tractates/${id}/yearplan.png`}
              alt="תוכנית שנתית"
              loading="lazy"
              onError={(e) => fallbackOnce(e, `/assets/cards/yearplan.webp`)}
            />
          </Link>
          <div className="c-card__body">
            <h3 className="c-card__title">
              <Link
                to={`/tractates/${id}/year-plan?domain=${domain}`}
                state={{ tractateName: tractate.name }}
              >
                תוכנית שנתית
              </Link>
            </h3>
            <p className="c-card__meta">{plansCount} קבצים</p>
          </div>
        </article>

        {/* Auxiliary folders for this tractate + domain */}
        {folders.map((folder) => {
          const to = `/tractates/${id}/auxiliary/${folder.id}?domain=${domain}`;
          const state = { tractateName: tractate.name };
          const count = folder.resources_count ?? 0;
          return (
            <article key={`aux-${folder.id}`} className="c-card">
              <Link to={to} state={state} aria-label={folder.title}>
                <img
                  className="c-card__media"
                  src={folder.image || '/assets/cards/chapter.png'}
                  alt={folder.title}
                  loading="lazy"
                  onError={(e) => fallbackOnce(e, '/assets/cards/chapter.png')}
                />
              </Link>
              <div className="c-card__body">
                <h3 className="c-card__title">
                  <Link to={to} state={state}>{folder.title}</Link>
                </h3>
                <p className="c-card__meta">{count} פריטים</p>
              </div>
            </article>
          );
        })}

        {/* Chapter cards for the current domain (pass state to TopicList) */}
        {chapters.map((ch) => {
        const label =
          ch.display || ch.title || (ch.number != null ? `פרק ${ch.number}` : 'פרק ללא שם');

        const fallback = '/assets/cards/chapter.png';
        const src = ch.image || fallback; // ← use DB image if present

        const link = `/tractates/${id}/topics?domain=${domain}&chapter=${ch.id}`;
        const state = { tractateName: tractate.name };

        return (
          <article key={ch.id} className="c-card">
            <Link to={link} state={state} aria-label={label}>
              <img
                className="c-card__media"
                src={src}
                alt={label}
                loading="lazy"
                onError={(e) => fallbackOnce(e, fallback)} // broken/missing → default
              />
            </Link>
            <div className="c-card__body">
              <h3 className="c-card__title">
                <Link to={link} state={state}>
                  {label}
                </Link>
              </h3>
              <p className="c-card__meta muted">
                פרקי {domain === 'toshba' ? 'תושב"ע' : 'תלמוד'}
              </p>
            </div>
          </article>
        );
      })}

      </div>

      {/* Empty state for chapters */}
      {chapters.length === 0 && (
        <p className="muted center" style={{ marginTop: '1rem' }}>
          אין פרקים להצגה בתחום הנבחר.
        </p>
      )}
    </div>
  );
}
